import { useMemo } from 'react';
import type {
  BatchIdentificationResults,
  BatchImportApplyResponse,
  BatchImportPreview,
  BatchTransferHistory,
  BatchTransferJob,
  ChecklistCorrectionsFile,
  ChecklistCorrectionPreview,
  ChecklistCorrectionApplyRow,
  CombinedIdentificationPackage,
  CombinedPackagePreview,
  BatchImportMode,
} from '@tcvs/shared';
import { useApi } from '../api';

export function useBatchTransferApi() {
  const api = useApi();
  return useMemo(() => {
    const path = (batchId: string) => `/review-batches/${encodeURIComponent(batchId)}/transfer`;
    return {
      stagePackage: (
        batchId: string,
        packageId: string,
        results: BatchIdentificationResults,
        corrections: ChecklistCorrectionsFile | undefined,
        importMode: BatchImportMode,
        signal?: AbortSignal,
      ) =>
        api.request<CombinedPackagePreview>(path(batchId), {
          method: 'POST',
          body: { action: 'STAGE_COMBINED_PACKAGE', packageId, results, corrections, importMode },
          signal,
        }),
      getPackage: (batchId: string, packageId: string, signal?: AbortSignal) =>
        api.request<{ package: CombinedIdentificationPackage; reportId?: string }>(path(batchId), {
          query: { packageId },
          signal,
        }),
      previewPackage: (batchId: string, packageId: string, signal?: AbortSignal) =>
        api.request<CombinedPackagePreview>(path(batchId), {
          method: 'POST',
          body: { action: 'PREVIEW_COMBINED_PACKAGE', packageId },
          signal,
        }),
      previewPackageIdentities: (batchId: string, packageId: string, signal?: AbortSignal) =>
        api.request<{
          package: CombinedIdentificationPackage;
          current: BatchImportPreview;
          reportId?: string;
        }>(path(batchId), {
          method: 'POST',
          body: { action: 'PREVIEW_PACKAGE_IDENTITIES', packageId },
          signal,
        }),
      applyPackageCorrections: (
        batchId: string,
        packageId: string,
        selectedCorrectionIds: string[],
        previewToken: string,
        signal?: AbortSignal,
      ) =>
        api.request<{ rows: ChecklistCorrectionApplyRow[]; reportId?: string }>(path(batchId), {
          method: 'POST',
          body: {
            action: 'APPLY_PACKAGE_CORRECTIONS',
            packageId,
            selectedCorrectionIds,
            previewToken,
          },
          signal,
        }),
      applyPackageIdentities: (
        batchId: string,
        packageId: string,
        selectedCardIds: string[],
        previewToken: string,
        attested: boolean,
        signal?: AbortSignal,
      ) =>
        api.request<BatchImportApplyResponse>(path(batchId), {
          method: 'POST',
          body: {
            action: 'APPLY_PACKAGE_IDENTITIES',
            packageId,
            selectedCardIds,
            previewToken,
            attested,
          },
          signal,
        }),
      previewCorrections: (
        batchId: string,
        corrections: ChecklistCorrectionsFile,
        signal?: AbortSignal,
      ) =>
        api.request<ChecklistCorrectionPreview & { reportId?: string }>(path(batchId), {
          method: 'POST',
          body: { action: 'PREVIEW_CHECKLIST_CORRECTIONS', corrections },
          signal,
        }),
      applyCorrections: (
        batchId: string,
        corrections: ChecklistCorrectionsFile,
        selectedCorrectionIds: string[],
        previewToken: string,
        signal?: AbortSignal,
      ) =>
        api.request<{ rows: ChecklistCorrectionApplyRow[]; reportId?: string }>(path(batchId), {
          method: 'POST',
          body: {
            action: 'APPLY_CHECKLIST_CORRECTIONS',
            corrections,
            selectedCorrectionIds,
            previewToken,
          },
          signal,
        }),
      createExport: (batchId: string, requestId: string, signal?: AbortSignal) =>
        api.request<BatchTransferJob>(path(batchId), {
          method: 'POST',
          body: { action: 'CREATE_EXPORT', requestId },
          signal,
        }),
      getJob: (batchId: string, jobId: string, signal?: AbortSignal) =>
        api.request<BatchTransferJob>(path(batchId), { query: { jobId }, signal }),
      logImportFailure: (
        batchId: string,
        reason:
          | 'INVALID_JSON'
          | 'INVALID_FORMAT'
          | 'FILE_TOO_LARGE'
          | 'WRONG_BATCH'
          | 'FILE_READ_FAILED',
        fileName: string,
        signal?: AbortSignal,
        importMode: 'DRAFT' | 'MANUAL_CONFIRMED' = 'DRAFT',
      ) =>
        api.request<{ reportId: string }>(path(batchId), {
          method: 'POST',
          body: { action: 'LOG_IMPORT_FAILURE', reason, fileName, importMode },
          signal,
        }),
      history: (batchId: string, signal?: AbortSignal) =>
        api.request<BatchTransferHistory>(path(batchId), { signal }),
      preview: (
        batchId: string,
        results: BatchIdentificationResults,
        signal?: AbortSignal,
        importMode: 'DRAFT' | 'MANUAL_CONFIRMED' = 'DRAFT',
      ) =>
        api.request<BatchImportPreview>(path(batchId), {
          method: 'POST',
          body: { action: 'PREVIEW_IMPORT', results, importMode },
          signal,
        }),
      apply: (
        batchId: string,
        results: BatchIdentificationResults,
        selectedCardIds: string[],
        previewToken: string,
        signal?: AbortSignal,
        importMode: 'DRAFT' | 'MANUAL_CONFIRMED' = 'DRAFT',
      ) =>
        api.request<BatchImportApplyResponse>(path(batchId), {
          method: 'POST',
          body: { action: 'APPLY_IMPORT', results, selectedCardIds, previewToken, importMode },
          signal,
        }),
    };
  }, [api]);
}
export type BatchTransferApi = ReturnType<typeof useBatchTransferApi>;
